import { APP_NAME } from "@zentrack/config";

import { SCHEMA_VERSION, type BrowserState } from "./state.js";

export const BACKUP_KIND = "zentrack-local-backup" as const;

/**
 * Schema versions this runtime knows how to import directly. Backups carrying a
 * version in this set are imported as-is. Any other version is rejected unless a
 * migration is registered for it below.
 *
 * Version 1 is the initial browser backup schema.
 */
export const SUPPORTED_SCHEMA_VERSIONS: ReadonlyArray<number> = [SCHEMA_VERSION];

/**
 * Registry of migrations from a source schema version to the next. Each entry
 * receives the raw data object from an older backup and must return data shaped
 * for the next version. The runtime walks the chain from the backup's version up
 * to the current SCHEMA_VERSION.
 *
 * Empty today — added so future schema bumps have an explicit, testable path
 * instead of silently overwriting state.
 */
export const BACKUP_MIGRATIONS: ReadonlyMap<number, (data: unknown) => unknown> = new Map();

export interface LocalBackup {
  kind: typeof BACKUP_KIND;
  schemaVersion: number;
  exportedAt: string;
  app: string;
  data: BrowserState;
}

/**
 * Build a JSON-serializable backup of the current browser state. The data field
 * is the whole persisted state, which already excludes runtime-only values
 * (timer "now", idle polling, etc.).
 */
export function buildBackup(state: BrowserState, exportedAt: Date = new Date()): LocalBackup {
  return {
    kind: BACKUP_KIND,
    schemaVersion: SCHEMA_VERSION,
    exportedAt: exportedAt.toISOString(),
    app: APP_NAME,
    data: state,
  };
}

export function serializeBackup(backup: LocalBackup): string {
  return JSON.stringify(backup, null, 2);
}

export interface BackupValidation {
  ok: boolean;
  backup: LocalBackup | null;
  /** Friendly, user-facing reason when validation fails. */
  error: string | null;
}

const REQUIRED_ARRAY_FIELDS: ReadonlyArray<keyof BrowserState> = [
  "clients",
  "projects",
  "projectSoftwareRules",
  "timeEntries",
  "appUsages",
];

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * A schema version is importable if it is directly supported, or if there is a
 * continuous migration chain from it up to the current SCHEMA_VERSION. Guards
 * against an infinite loop in case a migration map is misconfigured.
 */
function isImportableSchemaVersion(version: number): boolean {
  if (SUPPORTED_SCHEMA_VERSIONS.includes(version)) {
    return true;
  }

  let current = version;
  const guard = new Set<number>();

  while (current !== SCHEMA_VERSION) {
    if (guard.has(current)) {
      return false;
    }
    guard.add(current);

    const migrate = BACKUP_MIGRATIONS.get(current);
    if (!migrate) {
      return false;
    }

    // Migrations advance by exactly one version; if a migration ever targets a
    // larger jump it is misconfigured and we fail safe.
    current += 1;
  }

  return true;
}

/**
 * Validate that a parsed JSON value is a genuine ZenTrack local backup before
 * it is trusted to overwrite local data. Returns a friendly error instead of
 * throwing so callers can surface it via a shadcn Alert / toast.
 */
export function validateBackup(value: unknown): BackupValidation {
  if (!isObject(value)) {
    return { ok: false, backup: null, error: "This file is not a ZenTrack backup." };
  }

  if (value.kind !== BACKUP_KIND) {
    return {
      ok: false,
      backup: null,
      error: "This file does not look like a ZenTrack backup.",
    };
  }

  const schemaVersion = value.schemaVersion;
  if (typeof schemaVersion !== "number" || !Number.isFinite(schemaVersion)) {
    return {
      ok: false,
      backup: null,
      error: "The backup is missing a valid schema version.",
    };
  }

  if (!isImportableSchemaVersion(schemaVersion)) {
    const supported = SUPPORTED_SCHEMA_VERSIONS.slice().sort((a, b) => a - b).join(", ");
    return {
      ok: false,
      backup: null,
      error: `This backup uses schema version ${schemaVersion}, which this version of ZenTrack cannot import. Supported versions: ${supported}.`,
    };
  }

  const data = value.data;
  if (!isObject(data)) {
    return { ok: false, backup: null, error: "The backup is missing its data section." };
  }

  for (const field of REQUIRED_ARRAY_FIELDS) {
    if (!Array.isArray(data[field])) {
      return {
        ok: false,
        backup: null,
        error: `The backup data is missing the "${field}" list.`,
      };
    }
  }

  if (data.activeSession !== null && !isObject(data.activeSession)) {
    return {
      ok: false,
      backup: null,
      error: "The backup contains a malformed active session.",
    };
  }

  if (!isObject(data.settings)) {
    return { ok: false, backup: null, error: "The backup is missing its settings." };
  }

  return { ok: true, backup: value as unknown as LocalBackup, error: null };
}

/** Parse raw backup file text and validate it. Never throws. */
export function parseBackup(text: string): BackupValidation {
  let parsed: unknown;

  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, backup: null, error: "This file is not valid JSON." };
  }

  return validateBackup(parsed);
}
